import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { invalidateNeedsAttention } from '@/hooks/useNeedsAttention';
import { broadcastQueryInvalidation } from '@/hooks/useWorkspaceBroadcastSync';

/**
 * Leaf hook: fan a post mutation (media add/remove/reorder/edit, caption
 * rewrite, duplicate, delete, schedule) out to EVERY surface that renders the
 * post so nothing goes stale. Lifted out of PostBody (was an inline closure)
 * into a leaf hook so any caller — inspector bodies, the post media manager,
 * a future chat write-back — can invalidate the same canonical set WITHOUT a
 * back-import into the inspector tree (keeps analyze:circular green).
 *
 * The set adds ['scheduled-post-dates', userId, postId] (the calendar date-dot
 * layer) — editing a scheduled post's media previously left the calendar stale
 * — and ['post', postId, userId] (the usePost query key) to refetch getPost.
 * It also invalidates the two needs-attention queries (badge/banner/tab
 * Failed count) since a delete can remove a failed post, and broadcasts to
 * other tabs via BroadcastChannel.
 */
export function useInvalidatePostSurfaces(): (args: {
  userId: string;
  postId: string;
}) => Promise<void> {
  const queryClient = useQueryClient();

  return useCallback(
    async ({ userId, postId }) => {
      const keys: string[][] = [
        ['posts'],
        ['slot-posts'],
        ['unbound-plan-posts'],
        ['planSlots'],
        ['library'],
        ['media-library'],
        ['media'],
        ['scheduled-post-dates', userId, postId],
        // refetch getPost (the usePost query key in src/hooks/usePost.ts)
        ['post', postId, userId],
        // Publishing > Posts (CalendarListView) — omitted here previously, so
        // every caption/platform/media edit inside the post editor left that
        // list showing whatever it looked like right after the draft was
        // created, not the current row. Verified live: a caption typed and
        // committed, plus a platform attach, both landed correctly in
        // social_posts, but the list still rendered "Untitled post" with the
        // original single platform icon until something else (e.g. a delete)
        // happened to invalidate these keys.
        ['calendar-tab-posts'],
        ['calendar-tab-count'],
        ['drafts-infinite'],
        ['draft-posts-count'],
      ];
      await Promise.all(keys.map((queryKey) => queryClient.invalidateQueries({ queryKey })));
      await invalidateNeedsAttention(queryClient);
      // Additive predicate invalidations: match post-scoped keys by postId
      // regardless of userId so a null/empty userId degradation (e.g. a session
      // loading race) still refreshes the open inspector and calendar dots.
      // These supplement — not replace — the exact-key invalidations above.
      await Promise.all([
        // ['post', postId, userId] — usePost key (src/hooks/usePost.ts)
        queryClient.invalidateQueries({
          predicate: (q) => q.queryKey[0] === 'post' && q.queryKey[1] === postId,
        }),
        // ['scheduled-post-dates', userId, postId] — calendar date-dot key
        queryClient.invalidateQueries({
          predicate: (q) => q.queryKey[0] === 'scheduled-post-dates' && q.queryKey[2] === postId,
        }),
      ]);
      broadcastQueryInvalidation(keys);
    },
    [queryClient],
  );
}
